/**
 * Próximas sessões da Masterclass Allu — calculadas dinamicamente.
 *
 * Padrão: QUINTA às 19h00 BRT, semanal (America/Sao_Paulo, sem horário de verão).
 * Exceção pontual: a semana de 17/06/2026 cai na QUARTA (ver EXTRAS + EXCLUSIONS).
 *
 * Zero manutenção: o `getNextSession()` calcula em tempo real qual é a próxima
 * Ter/Qui no futuro a partir do `now`. Quando uma sessão passa, a próxima vira
 * a próxima Ter/Qui automaticamente — sem precisar editar arquivo nenhum.
 *
 * Pra cenários especiais:
 * - `EXCLUSIONS`: pular uma data específica (ex: feriado). Formato "YYYY-MM-DD".
 * - `EXTRAS`: adicionar sessão fora do padrão. Formato ISO completo com -03:00.
 */

export interface Session {
  /** ISO 8601 com timezone -03:00 (BRT). Ex: "2026-06-11T19:00:00-03:00". */
  iso: string;
  /** Date object da sessão. */
  date: Date;
  /** Webinar ID do Zoom (se já criado). Preencher quando precisar. */
  zoomWebinarId?: string;
}

// Configuração do padrão
const SESSION_WEEKDAYS_BRT = [4] as const; // 4 = Quinta (semanal)
const SESSION_HOUR_BRT = 19;
const SESSION_TZ_OFFSET = "-03:00";

// Cenários especiais (vazios por padrão — preencher se precisar)
const EXCLUSIONS: string[] = [
  "2026-06-04", // pré-lançamento (primeira live oficial = 11/06)
  "2026-06-09", // pré-lançamento (primeira live oficial = 11/06)
  "2026-06-18", // semana de 17/06 é na QUARTA (ver EXTRAS) — pula a quinta
];
const EXTRAS: string[] = [
  "2026-06-17T19:00:00-03:00", // exceção: semana de 17/06 cai na quarta
  // Ex: "2026-08-15T19:00:00-03:00" pra adicionar sessão extra
];

/**
 * Extrai (ano, mês, dia, weekday) de um Date como se fosse em BRT.
 * Robusto contra qualquer timezone do navegador do usuário.
 */
function getBrtParts(d: Date): {
  year: number;
  month: number;
  day: number;
  weekday: number;
} {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(d).map((p) => [p.type, p.value])
  );
  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return {
    year: parseInt(parts.year),
    month: parseInt(parts.month),
    day: parseInt(parts.day),
    weekday: weekdayMap[parts.weekday] ?? 0,
  };
}

const pad = (n: number) => n.toString().padStart(2, "0");

function buildSessionIso(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}T${pad(SESSION_HOUR_BRT)}:00:00${SESSION_TZ_OFFSET}`;
}

/**
 * Retorna a próxima sessão futura a partir de `now`.
 *
 * Algoritmo:
 *   1. Gera os candidatos do padrão (próximas 4 ter/qui)
 *   2. Aplica EXCLUSIONS (remove datas bloqueadas)
 *   3. Aplica EXTRAS (mescla sessões one-off)
 *   4. Filtra só as futuras
 *   5. Retorna a mais próxima
 */
export function getNextSession(now: Date = new Date()): Session {
  const candidates: string[] = [];

  // Olha os próximos 21 dias e coleta as ter/qui do padrão
  for (let i = 0; i < 21; i++) {
    const day = new Date(now.getTime() + i * 86_400_000);
    const parts = getBrtParts(day);
    if (!SESSION_WEEKDAYS_BRT.includes(parts.weekday as 4)) continue;
    const dateKey = `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
    if (EXCLUSIONS.includes(dateKey)) continue;
    candidates.push(buildSessionIso(parts.year, parts.month, parts.day));
  }

  // Junta com EXTRAS
  const all = [...candidates, ...EXTRAS];

  // Filtra futuras e ordena
  const future = all
    .map((iso) => ({ iso, date: new Date(iso) }))
    .filter((s) => s.date.getTime() > now.getTime())
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  // Em condições normais sempre tem algo (ter/qui em 21 dias)
  if (future.length === 0) {
    // Fallback paranoico: 7 dias no futuro às 19h BRT
    const fallbackDate = new Date(now.getTime() + 7 * 86_400_000);
    const parts = getBrtParts(fallbackDate);
    const iso = buildSessionIso(parts.year, parts.month, parts.day);
    return { iso, date: new Date(iso) };
  }

  return future[0];
}

/**
 * Formata data + hora em pt-BR (ex: "Quinta, 11 de junho · 19h").
 * Usa Intl pra garantir TZ correto (BRT) independente do navegador.
 */
export function formatSessionDisplay(date: Date): string {
  const fmt = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(date).map((p) => [p.type, p.value])
  );
  const weekdayCap =
    (parts.weekday ?? "").charAt(0).toUpperCase() +
    (parts.weekday ?? "").slice(1).replace("-feira", "");
  return `${weekdayCap}, ${parts.day} de ${parts.month} · ${parts.hour}h`;
}

/**
 * Versão curta pra header / sticky CTA (ex: "Qui 11/Jun · 19h").
 */
export function formatSessionShort(date: Date): string {
  const weekdaysShort = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const monthsShort = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];
  // Pega componentes em BRT
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    month: "numeric",
    day: "numeric",
    weekday: "short",
    hour: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(date).map((p) => [p.type, p.value])
  );
  const weekdayIdx: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  const wIdx = weekdayIdx[parts.weekday] ?? 0;
  const mIdx = parseInt(parts.month) - 1;
  return `${weekdaysShort[wIdx]} ${parts.day}/${monthsShort[mIdx]} · ${parts.hour}h`;
}
